import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import moment from 'moment'
import { IBillDocument, IExtractedField } from '../../../../shared/services/file-upload/file-upload.model'
import { BILL_FIELD_LABELS, BILL_KEY_FIELDS, isLowConfidence, lowConfidenceFields } from '../../../../shared/utils/bill-upload-utils'

/**
 * BillDocumentDetail
 * -------------------------------------------------------------------------
 * What was read off one bill, for a reviewer to check against the document: the usage figures, the
 * key fields with how sure the read was, and any note about the bill (could not be read, credit
 * note, held back by a credit note, possible duplicate). Extraction is a best guess, so low
 * confidence is a prompt to look, never a block.
 *
 * Shown in the bill drawer on the import review step and on the upload's Bills tab.
 */

const FieldValue: FunctionComponent<{ fieldKey: string, field: IExtractedField }> = ({ fieldKey, field }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5 }}>
    <Typography variant='body2' sx={{ color: 'text.secondary', flexShrink: 0 }}>
      {BILL_FIELD_LABELS[fieldKey] || fieldKey}
    </Typography>
    <Stack direction='row' spacing={1} alignItems='center' sx={{ minWidth: 0 }}>
      <Typography variant='body2' noWrap title={String(field.value)}>
        {String(field.value)}
      </Typography>
      {isLowConfidence(field) && (
        <Chip
          size='small'
          color='warning'
          variant='outlined'
          label={`${Math.round(field.confidence as number)}% sure`}
          title='This value is less certain. Please check it against the bill.'
        />
      )}
    </Stack>
  </Box>
)

export const BillDocumentDetail: FunctionComponent<{
  document: IBillDocument
  fileUploadId: string
}> = ({ document, fileUploadId }) => {
  const fields = document.extractedFields || {}
  const shown = BILL_KEY_FIELDS.filter((key) => fields[key])
  const lowConfidence = lowConfidenceFields(document)

  return (
    <Stack spacing={1}>
      {document.extractionStatus === 'FAILED' && (
        <Alert severity='error'>
          {document.errorMessage || 'This document could not be read.'} Its row is blank for you to fill in from the bill.
        </Alert>
      )}

      {!!document.cancelledBy && (
        <Alert severity='warning'>
          Cancelled by {document.cancelledBy.filename}, a credit note in this upload. Its rows are held back: discard
          them, and the replacement bill imports on its own row.
        </Alert>
      )}

      {!!document.duplicateOfBillDocumentId && (
        <Alert severity='info'>
          {document.duplicateOf
            ? (document.duplicateOf.fileUploadId === fileUploadId
              ? `Looks like the same bill as ${document.duplicateOf.filename} in this upload: same meter and billing period.`
              : `Looks like the same bill as ${document.duplicateOf.filename}, uploaded ${moment(document.duplicateOf.uploadedAt).format('DD/MM/YYYY')}: same meter and billing period.`)
            : 'Same meter and billing period as a bill already imported.'}
          {' '}Check before importing to avoid double counting.
        </Alert>
      )}

      {!!document.renewableEvidence && (
        <Alert severity={document.renewableDecision === 'confirmed' ? 'success' : 'info'}>
          This bill says: "{document.renewableEvidence.text}"{document.renewableEvidence.page ? ` (page ${document.renewableEvidence.page})` : ''}.{' '}
          {document.renewableDecision === 'confirmed'
            ? `Confirmed as renewable${document.renewableDecidedAt ? ` on ${moment(document.renewableDecidedAt).format('DD/MM/YYYY')}` : ''}: its electricity is recorded at 0 market-based.`
            : document.renewableDecision === 'dismissed'
              ? 'Marked as not renewable, so its electricity uses the usual market-based factor.'
              : typeof document.renewableEvidence.coveragePercent === 'number' && document.renewableEvidence.coveragePercent < 99.95
                ? `The certificates cover ${document.renewableEvidence.coveragePercent}% of its usage, so set the rate on the row by hand.`
                : 'Not confirmed yet.'}
        </Alert>
      )}

      {document.extractionStatus === 'COMPLETED' && (
        <>
          {/* A read bill can still carry a note: a credit note, or a usage figure that could not be
              confirmed. Without it the reviewer cannot tell an unusual bill from a broken read. */}
          {!!document.errorMessage && (
            <Alert severity={document.isCreditNote ? 'info' : 'warning'}>
              {document.errorMessage}
            </Alert>
          )}
          {!!document.isCreditNote && (document.cancelledEmissionIds || []).length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(document.cancelledEmissionIds || []).map((emissionId, index) => (
                <Button
                  key={emissionId}
                  size='small'
                  variant='outlined'
                  component='a'
                  href={`/emissions/overview/${emissionId}`}
                  target='_blank'
                  rel='noopener'
                >
                  <Typography variant='body2'>
                    {(document.cancelledEmissionIds || []).length > 1
                      ? `Open the original emission (${index + 1})`
                      : 'Open the original emission'}
                  </Typography>
                </Button>
              ))}
            </Box>
          )}
          {lowConfidence.length > 0 && (
            <Alert severity='warning'>
              Please double-check the highlighted {lowConfidence.length === 1 ? 'value' : 'values'} against the bill.
            </Alert>
          )}
          {(document.usage || []).length > 0 && (
            <Box>
              {(document.usage || []).map((usage, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, py: 0.5 }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', flexShrink: 0 }}>
                    {usage.label ? `Usage (${usage.label})` : 'Usage'}
                  </Typography>
                  <Typography variant='body2'>
                    {usage.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {usage.unit ? ` ${usage.unit}` : ''}
                    {usage.lines > 1 ? ` across ${usage.lines} rows` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box>
            {shown.map((key) => (
              <FieldValue key={key} fieldKey={key} field={fields[key]} />
            ))}
          </Box>
          {shown.length === 0 && !document.isCreditNote && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              Nothing useful could be found on this bill. Enter the figures by hand on its row.
            </Typography>
          )}
        </>
      )}
    </Stack>
  )
}
